/**
 * Observed vs estimated card state (docs/04 §0).
 * Estimates are computed, never stored as observations. Purchases never write observations.
 */

import { asCents, type Cents } from './money';
import { businessDate, compareBusinessDates, diffDays, type BusinessDate } from './time';

const DEBIT_KINDS = new Set([
  'purchase',
  'fee',
  'interest',
  'cash_advance',
  'balance_transfer',
  'adjustment',
]);
const CREDIT_KINDS = new Set(['refund', 'statement_credit']);

export type FinancialObservationInput = {
  id: string;
  card_id: string;
  deleted_at: string | null;
  observed_at: string;
  source: string;
  evidence_source_id: string;
  current_balance_cents: Cents | null;
  pending_balance_cents: Cents | null;
  statement_balance_cents: Cents | null;
  minimum_payment_cents: Cents | null;
  available_credit_cents: Cents | null;
  credit_limit_cents: Cents | null;
  cash_advance_limit_cents: Cents | null;
  payment_due_date: string | null;
  statement_closing_date: string | null;
  next_closing_date: string | null;
  purchase_apr_bps: number | null;
  promotional_apr_bps: number | null;
  promotional_apr_end: string | null;
  account_restricted: boolean | null;
  last_payment_date: string | null;
  last_payment_amount_cents: Cents | null;
  reflects_txn_ids: readonly string[];
  same_day_ambiguous: boolean;
  retracts_fields: readonly string[];
  retraction_reason: string | null;
};

export type TransactionInput = {
  id: string;
  card_id: string;
  deleted_at: string | null;
  kind: string;
  status: string;
  amount_cents: Cents;
  posted_date: string | null;
  transaction_date: string;
};

export type CardPaymentInput = {
  id: string;
  card_id: string;
  deleted_at: string | null;
  status: string;
  amount_cents: Cents;
  posted_date: string | null;
};

export type CardState = {
  card_id: string;
  observed: {
    current_balance_cents: Cents | null;
    current_balance_cents_observed_at: string | null;
    current_balance_cents_source: string | null;
    available_credit_cents: Cents | null;
    available_credit_cents_observed_at: string | null;
    available_credit_cents_source: string | null;
    credit_limit_cents: Cents | null;
    credit_limit_cents_observed_at: string | null;
    credit_limit_cents_source: string | null;
    statement_balance_cents: Cents | null;
    statement_balance_cents_observed_at: string | null;
    payment_due_date: string | null;
    payment_due_date_observed_at: string | null;
    statement_closing_date: string | null;
    statement_closing_date_observed_at: string | null;
    next_closing_date: string | null;
    next_closing_date_observed_at: string | null;
  };
  estimated_posted_balance: Cents | null;
  estimated_pending_balance: Cents;
  estimated_available_credit: Cents | null;
  unreconciled_delta: Cents | null;
  /** asOf − date(current_balance observed_at). Null when no balance observation. */
  state_age_days: number | null;
  same_day_ambiguous: boolean;
};

export type GetCardStateArgs = {
  cardId: string;
  asOf: BusinessDate;
  observations: readonly FinancialObservationInput[];
  transactions: readonly TransactionInput[];
  payments: readonly CardPaymentInput[];
};

/** Civil date of an observation timestamptz (ISO prefix). */
export function observationBusinessDate(observedAt: string): BusinessDate {
  return businessDate(observedAt.slice(0, 10));
}

function liveObservations(
  cardId: string,
  observations: readonly FinancialObservationInput[],
): FinancialObservationInput[] {
  return observations.filter((o) => o.card_id === cardId && o.deleted_at === null);
}

/** Latest row by (observed_at DESC, id DESC) — estimate baseline (docs/04 §0). */
export function latestFinancialObservation(
  cardId: string,
  observations: readonly FinancialObservationInput[],
): FinancialObservationInput | null {
  const live = liveObservations(cardId, observations);
  if (live.length === 0) return null;
  return live.reduce((best, row) => {
    if (row.observed_at > best.observed_at) return row;
    if (row.observed_at === best.observed_at && row.id > best.id) return row;
    return best;
  });
}

type FieldKey = keyof FinancialObservationInput;

function fieldRetractedAt(
  live: readonly FinancialObservationInput[],
  field: string,
): string | null {
  let max: string | null = null;
  for (const o of live) {
    if (!o.retracts_fields.includes(field)) continue;
    if (max === null || o.observed_at > max) max = o.observed_at;
  }
  return max;
}

function latestNonNullField<K extends FieldKey>(
  live: readonly FinancialObservationInput[],
  field: K,
): FinancialObservationInput | null {
  const retractedAt = fieldRetractedAt(live, String(field));
  const candidates = live.filter((o) => {
    const value = o[field];
    if (value === null || value === undefined) return false;
    if (retractedAt !== null && !(o.observed_at > retractedAt)) return false;
    return true;
  });
  if (candidates.length === 0) return null;
  return candidates.reduce((best, row) => {
    if (row.observed_at > best.observed_at) return row;
    if (row.observed_at === best.observed_at && row.id > best.id) return row;
    return best;
  });
}

/**
 * inBaselineWindow(txn, baseline) — docs/04 §0.
 * Same-calendar-day defaults to false (conservative); caller may surface ambiguity.
 */
export function inBaselineWindow(
  txn: TransactionInput,
  baseline: FinancialObservationInput,
): { included: boolean; sameDayAmbiguous: boolean } {
  if (baseline.reflects_txn_ids.includes(txn.id)) {
    return { included: false, sameDayAmbiguous: false };
  }
  if (txn.posted_date === null) {
    return { included: false, sameDayAmbiguous: false };
  }
  const posted = businessDate(txn.posted_date);
  const baselineDay = observationBusinessDate(baseline.observed_at);
  const cmp = compareBusinessDates(posted, baselineDay);
  if (cmp > 0) return { included: true, sameDayAmbiguous: false };
  if (cmp === 0) return { included: false, sameDayAmbiguous: true };
  return { included: false, sameDayAmbiguous: false };
}

function clampAvailable(value: Cents, creditLimit: Cents | null): Cents {
  let n = value;
  if (n < 0n) n = asCents(0n);
  if (creditLimit !== null && n > creditLimit) n = creditLimit;
  return n;
}

/**
 * Pure card state. Does not read or write IndexedDB / observations storage.
 * Recording purchases is the caller's ledger append — this function only computes.
 */
export function getCardState(args: GetCardStateArgs): CardState {
  const { cardId, asOf, observations, transactions, payments } = args;
  const live = liveObservations(cardId, observations);
  const baseline = latestFinancialObservation(cardId, observations);

  const balRow = latestNonNullField(live, 'current_balance_cents');
  const availRow = latestNonNullField(live, 'available_credit_cents');
  const limRow = latestNonNullField(live, 'credit_limit_cents');
  const stmtRow = latestNonNullField(live, 'statement_balance_cents');
  const dueRow = latestNonNullField(live, 'payment_due_date');
  const closeRow = latestNonNullField(live, 'statement_closing_date');
  const nextCloseRow = latestNonNullField(live, 'next_closing_date');

  const observedBalance = balRow?.current_balance_cents ?? null;
  const observedAvailable = availRow?.available_credit_cents ?? null;
  const observedLimit = limRow?.credit_limit_cents ?? null;

  let estimatedPosted: Cents | null = null;
  let estimatedAvailable: Cents | null = null;
  let sameDayAmbiguous = false;
  let pendingSum = 0n;

  const cardTxns = transactions.filter((t) => t.card_id === cardId && t.deleted_at === null);
  const cardPayments = payments.filter((p) => p.card_id === cardId && p.deleted_at === null);

  for (const txn of cardTxns) {
    if (txn.status === 'pending') {
      pendingSum += txn.amount_cents;
    }
  }

  // Estimates start from the latest observation ROW (docs/04 §0), not per-field winners.
  if (baseline !== null) {
    const baselineBalance = baseline.current_balance_cents;
    const baselineAvailable = baseline.available_credit_cents;
    const baselineLimit = baseline.credit_limit_cents;

    if (baselineBalance !== null) {
      let posted = baselineBalance;
      let available = baselineAvailable;

      for (const txn of cardTxns) {
        if (txn.status !== 'posted') continue;
        const { included, sameDayAmbiguous: amb } = inBaselineWindow(txn, baseline);
        if (amb) sameDayAmbiguous = true;
        if (!included) continue;

        if (DEBIT_KINDS.has(txn.kind)) {
          posted = asCents(posted + txn.amount_cents);
          if (available !== null && txn.kind === 'purchase') {
            available = asCents(available - txn.amount_cents);
          }
        } else if (CREDIT_KINDS.has(txn.kind)) {
          posted = asCents(posted - txn.amount_cents);
          if (available !== null) {
            available = asCents(available + txn.amount_cents);
          }
        }
      }

      const baselineDay = observationBusinessDate(baseline.observed_at);
      for (const pay of cardPayments) {
        if (pay.status !== 'posted' || pay.posted_date === null) continue;
        if (compareBusinessDates(businessDate(pay.posted_date), baselineDay) <= 0) continue;
        posted = asCents(posted - pay.amount_cents);
        if (available !== null) {
          available = asCents(available + pay.amount_cents);
        }
      }

      estimatedPosted = posted;
      estimatedAvailable =
        available === null ? null : clampAvailable(available, baselineLimit ?? observedLimit);
    } else {
      for (const txn of cardTxns) {
        if (txn.status !== 'posted') continue;
        const { sameDayAmbiguous: amb } = inBaselineWindow(txn, baseline);
        if (amb) sameDayAmbiguous = true;
      }
    }
  }

  if (baseline?.same_day_ambiguous) sameDayAmbiguous = true;

  let stateAgeDays: number | null = null;
  if (balRow !== null) {
    stateAgeDays = diffDays(observationBusinessDate(balRow.observed_at), asOf);
  }

  const unreconciled =
    estimatedPosted !== null && observedBalance !== null
      ? asCents(estimatedPosted - observedBalance)
      : null;

  return {
    card_id: cardId,
    observed: {
      current_balance_cents: observedBalance,
      current_balance_cents_observed_at: balRow?.observed_at ?? null,
      current_balance_cents_source: balRow?.source ?? null,
      available_credit_cents: observedAvailable,
      available_credit_cents_observed_at: availRow?.observed_at ?? null,
      available_credit_cents_source: availRow?.source ?? null,
      credit_limit_cents: observedLimit,
      credit_limit_cents_observed_at: limRow?.observed_at ?? null,
      credit_limit_cents_source: limRow?.source ?? null,
      statement_balance_cents: stmtRow?.statement_balance_cents ?? null,
      statement_balance_cents_observed_at: stmtRow?.observed_at ?? null,
      payment_due_date: dueRow?.payment_due_date ?? null,
      payment_due_date_observed_at: dueRow?.observed_at ?? null,
      statement_closing_date: closeRow?.statement_closing_date ?? null,
      statement_closing_date_observed_at: closeRow?.observed_at ?? null,
      next_closing_date: nextCloseRow?.next_closing_date ?? null,
      next_closing_date_observed_at: nextCloseRow?.observed_at ?? null,
    },
    estimated_posted_balance: estimatedPosted,
    estimated_pending_balance: asCents(pendingSum),
    estimated_available_credit: estimatedAvailable,
    unreconciled_delta: unreconciled,
    state_age_days: stateAgeDays,
    same_day_ambiguous: sameDayAmbiguous,
  };
}
